/**
 * Live module step 2 — Mitarbeiter (PRD §4.3/§4.4), ported from the reference
 * `projects.$projectId.live.tsx` step 2 + its `EmployeesManager`.
 *
 * The reference's two-column "Excel template (bulk) / Preview" section is kept, but the parsing
 * and committing runs through our existing `ImportDialog`: the workbook is parsed on the server so
 * preview and commit judge rows by the same rules (PLAN.md D-24/D-25), which an inline
 * browser-side dropzone would break. The roster below is this project's people only — the person
 * still belongs to the company, so master-data edits reuse the `/employees` dialogs, and
 * `canEdit` / `canRemove` keep coming from the server per assignment (PLAN.md D-22).
 *
 * No tracking tokens: the reference's "copy login link" is a permanent bearer URL, which PRD §2.2
 * forbids. Inviting sends the one-time magic link by email instead.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MoreHorizontal, Pencil, Plus, Send, Upload, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AddEmployeeDialog,
  EditEmployeeOnProjectDialog,
} from "@/components/employees/employee-dialogs";
import { ImportDialog } from "@/components/employees/import-dialog";
import { EmployeeNameCell, TwoLineTableHead } from "@/components/employees/person-status-badges";
import { toastApiError } from "@/lib/rate-limit";
import { formatDate, formatHours, formatPercent } from "@/lib/format";
import { useEmployees, useInvitePeople } from "@/lib/tenancy";
import { isNeverSignedIn, type PersonSummary, type ProjectSummary } from "@/shared/schemas/tenancy";

export function ProjectEmployeesStep({
  project,
  canManage,
}: {
  project: ProjectSummary;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const people = useEmployees(project.companyId);
  const invite = useInvitePeople(project.companyId);

  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  /** This project's roster: a person is on it when they hold a live assignment to it. */
  const roster = useMemo(
    () =>
      (people.data ?? []).filter((person) =>
        person.assignments.some(
          (assignment) => assignment.projectId === project.id && !assignment.archived,
        ),
      ),
    [people.data, project.id],
  );

  const uninvited = roster.filter((person) => !person.archived && isNeverSignedIn(person));

  function inviteMany(userIds: string[]) {
    if (userIds.length === 0) return;
    invite.mutate(
      { userIds },
      {
        onSuccess: (data) => {
          const sent = data.results.filter(
            (entry) => entry.status === "queued" || entry.status === "deduped",
          ).length;
          toast.success(t("employees.inviteResult", { sent, skipped: data.results.length - sent }));
        },
        onError: (error) => toastApiError(error),
      },
    );
  }

  return (
    <div className="mt-10 space-y-10" data-testid="live-employees-step">
      {canManage && (
        <section className="grid gap-8 md:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="text-2xl">{t("projectLive.bulkTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("projectLive.bulkBody")}</p>
            <Button className="mt-4 w-full" onClick={() => setImporting(true)}>
              <Upload className="size-4" /> {t("employees.import")}
            </Button>
          </div>
          <div>
            <h2 className="text-2xl">{t("projectLive.rosterTitle", { count: roster.length })}</h2>
            {roster.length === 0 ? (
              <div className="mt-4 grid place-items-center gap-3 rounded-2xl border border-dashed border-border bg-surface p-10 text-sm text-muted-foreground">
                <p>{t("projectLive.rosterEmptyHint")}</p>
                <Button onClick={() => setAdding(true)}>
                  <Plus className="size-4" /> {t("employees.add")}
                </Button>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-6 text-sm">
                <Users className="size-4 text-muted-foreground" />
                <span>{t("projectsList.people", { count: roster.length })}</span>
                {uninvited.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={invite.isPending}
                    onClick={() => inviteMany(uninvited.map((person) => person.id))}
                  >
                    <Send className="size-4" />
                    {t("employees.inviteMany", { count: uninvited.length })}
                  </Button>
                )}
                <Button size="sm" onClick={() => setAdding(true)}>
                  <Plus className="size-4" /> {t("employees.add")}
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-2xl">{t("projectLive.rosterHeading")}</h2>
        <div className="mt-4 rounded-2xl border border-border bg-card shadow-soft">
          {people.isLoading && !people.data ? (
            <Skeleton className="m-6 h-32" />
          ) : roster.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">
              {t("projectLive.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("employees.colName")}</TableHead>
                  <TableHead>{t("employees.colPosition")}</TableHead>
                  <TableHead>{t("employees.colEmail")}</TableHead>
                  <TwoLineTableHead
                    className="w-24 text-right"
                    testId="live-employees-col-hours"
                    line1={t("employees.colHoursLine1")}
                    line2={t("employees.colHoursLine2")}
                  />
                  <TableHead className="w-28 text-right">
                    {t("employees.assignRdShareColumnHeader")}
                  </TableHead>
                  <TableHead className="w-36">{t("employees.assignEntryDate")}</TableHead>
                  <TableHead className="w-40 text-right">{t("employees.colActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roster.map((person) => (
                  <RosterRow
                    key={person.id}
                    person={person}
                    project={project}
                    onInvite={() => inviteMany([person.id])}
                    inviting={invite.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      {adding && (
        <AddEmployeeDialog
          open
          onOpenChange={setAdding}
          companyId={project.companyId}
          projects={[project]}
        />
      )}
      {importing && (
        <ImportDialog
          open
          onOpenChange={setImporting}
          companyId={project.companyId}
          projects={[project]}
          onImported={() => void people.refetch()}
        />
      )}
    </div>
  );
}

function RosterRow({
  person,
  project,
  onInvite,
  inviting,
}: {
  person: PersonSummary;
  project: ProjectSummary;
  onInvite: () => void;
  inviting: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  const assignment = person.assignments.find(
    (entry) => entry.projectId === project.id && !entry.archived,
  );
  const neverSignedIn = isNeverSignedIn(person);

  return (
    <TableRow className={person.archived ? "opacity-60" : undefined}>
      <TableCell>
        <EmployeeNameCell person={person} />
      </TableCell>
      <TableCell className="text-muted-foreground" data-testid={`employee-position-${person.id}`}>
        {person.position ?? "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">{person.email}</TableCell>
      <TableCell className="text-right tabular-nums">
        {person.weeklyHours === null ? "—" : formatHours(person.weeklyHours)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {assignment ? formatPercent(assignment.rdShare) : "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {assignment ? formatDate(assignment.entryDate) : "—"}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("employees.colActions")}
                data-testid={`pdp-roster-actions-${person.id}`}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {neverSignedIn && (
                <DropdownMenuItem disabled={inviting} onSelect={onInvite}>
                  <Send className="size-4" /> {t("employees.invite")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => setEditing(true)}
                data-testid={`pdp-roster-edit-${person.id}`}
              >
                <Pencil className="size-4" /> {t("employees.edit")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>

      {editing && (
        <EditEmployeeOnProjectDialog
          person={person}
          assignment={assignment ?? null}
          project={project}
          onOpenChange={() => setEditing(false)}
        />
      )}
    </TableRow>
  );
}
